import { useCallback, useEffect, useRef, useState } from 'react';
import type { PaginatedMeta } from '../../services/onboardingTypes';

export type PaginatedFetchParams = {
  pageNumber: number;
  pageSize: number;
  search?: string;
  signal?: AbortSignal;
  [key: string]: unknown;
};

export type PaginatedFetchResult<T> = PaginatedMeta & {
  items: T[];
};

export type UsePaginatedListOptions<T> = {
  pageSize?: number;
  fetchPage: (params: PaginatedFetchParams) => Promise<PaginatedFetchResult<T>>;
  filters?: Record<string, unknown>;
  search?: string;
  enabled?: boolean;
  getItemKey?: (item: T) => string | number;
};

export type UsePaginatedListReturn<T> = {
  items: T[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  totalCount: number;
  error: string | null;
  loadMore: () => void;
  refresh: () => void;
  removeItem: (key: string | number) => void;
};

function mergeItems<T>(
  prev: T[],
  next: T[],
  replace: boolean,
  getItemKey?: (item: T) => string | number
): T[] {
  if (replace) return next;
  if (!getItemKey) return [...prev, ...next];
  const seen = new Set(prev.map(getItemKey));
  return [...prev, ...next.filter((item) => !seen.has(getItemKey(item)))];
}

export function usePaginatedList<T>({
  pageSize = 25,
  fetchPage,
  filters = {},
  search = '',
  enabled = true,
  getItemKey,
}: UsePaginatedListOptions<T>): UsePaginatedListReturn<T> {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const getItemKeyRef = useRef(getItemKey);
  getItemKeyRef.current = getItemKey;

  const filtersKey = JSON.stringify(filters);
  const searchKey = search.trim();

  const queryRef = useRef({
    enabled,
    pageSize,
    filtersKey,
    searchKey,
  });
  queryRef.current = { enabled, pageSize, filtersKey, searchKey };

  const pageRef = useRef(page);
  pageRef.current = page;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const isLoadingMoreRef = useRef(isLoadingMore);
  isLoadingMoreRef.current = isLoadingMore;

  const requestGenerationRef = useRef(0);

  const runFetch = useCallback(
    async (
      pageNumber: number,
      replace: boolean,
      signal?: AbortSignal,
      generation?: number
    ): Promise<void> => {
      const query = queryRef.current;
      if (!query.enabled) return;

      const isFirstPage = pageNumber === 0;
      if (isFirstPage) setIsLoading(true);
      else setIsLoadingMore(true);
      setError(null);

      try {
        const result = await fetchPageRef.current({
          pageNumber,
          pageSize: query.pageSize,
          search: query.searchKey || undefined,
          signal,
          ...(JSON.parse(query.filtersKey) as Record<string, unknown>),
        });
        if (signal?.aborted) return;
        if (generation != null && generation !== requestGenerationRef.current) {
          return;
        }

        setItems((prev) =>
          mergeItems(prev, result.items, replace, getItemKeyRef.current)
        );
        setPage(pageNumber);
        setHasMore(result.has_more);
        setTotalCount(result.total_count);
      } catch (err: unknown) {
        if (signal?.aborted) return;
        if (generation != null && generation !== requestGenerationRef.current) {
          return;
        }
        setError(
          err instanceof Error ? err.message : 'Unable to load data.'
        );
        if (replace) {
          setItems([]);
          setHasMore(false);
          setTotalCount(0);
        }
      } finally {
        if (signal?.aborted) return;
        if (generation != null && generation !== requestGenerationRef.current) {
          return;
        }
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setHasMore(false);
      setTotalCount(0);
      setIsLoading(false);
      setIsLoadingMore(false);
      return;
    }

    const generation = ++requestGenerationRef.current;
    setItems([]);
    setHasMore(false);
    setTotalCount(0);
    const controller = new AbortController();
    void runFetch(0, true, controller.signal, generation);
    return () => controller.abort();
  }, [enabled, filtersKey, pageSize, searchKey, runFetch]);

  const loadMore = useCallback(() => {
    if (!queryRef.current.enabled) return;
    if (isLoadingRef.current || isLoadingMoreRef.current || !hasMoreRef.current) {
      return;
    }
    void runFetch(pageRef.current + 1, false);
  }, [runFetch]);

  const refresh = useCallback(() => {
    const generation = ++requestGenerationRef.current;
    void runFetch(0, true, undefined, generation);
  }, [runFetch]);

  const removeItem = useCallback((key: string | number) => {
    const getKey = getItemKeyRef.current;
    if (!getKey) return;
    setItems((prev) => {
      const next = prev.filter((item) => getKey(item) !== key);
      if (next.length === prev.length) return prev;
      setTotalCount((count) => Math.max(0, count - 1));
      return next;
    });
  }, []);

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    error,
    loadMore,
    refresh,
    removeItem,
  };
}
